import React from "react";
import { RegisterOptions, useController, useFormContext } from "react-hook-form";
import DocumentPicturePreview from "./DocumentPicturePreview";
import DocumentFileButton from "./DocumentFileButton";
import DocumentImageViewer from "./DocumentImageViewer";
import useShowImage from "../../../../hooks/useShowImage";
import useImage from "./useImage";

type FormDocumentPicturePreviewProps = {
  name: string;
  label: string;
  rules?: RegisterOptions;
  previewHeight?: number | string;
  placeholderHeight?: number | string;
  backgroundColor?: string;
  borderStyle?: "normal" | "dashed";
  iconVariant?: "default" | "circularImage";
  allowClear?: boolean;
  allowImageViewer?: boolean;
  displayVariant?: "camera" | "file";
  readOnly?: boolean;
  fileName?: string;
};

const FormDocumentPicturePreview: React.FC<FormDocumentPicturePreviewProps> = ({
  name,
  label,
  rules,
  previewHeight,
  placeholderHeight,
  backgroundColor,
  borderStyle,
  iconVariant,
  allowClear,
  allowImageViewer,
  displayVariant = "camera",
  readOnly = false,
  fileName,
}) => {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
    rules,
  });

  const getImage = useShowImage();
  const { uploadImage } = useImage();
  const [imageUrl, setImageUrl] = React.useState("");
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);

  React.useEffect(() => {
    if (field.value instanceof File) {
      const fileUrl = URL.createObjectURL(field.value);
      setImageUrl(fileUrl);
      return () => URL.revokeObjectURL(fileUrl);
    } else if (typeof field.value === "string" && field.value) {
      setImageUrl(getImage(field.value));
      return undefined;
    }

    setImageUrl("");
    return undefined;
  }, [field.value, getImage]);

  const handleUpload = (file: File | null, dataUrl?: string) => {
    field.onChange(file || null);
    if (file && dataUrl) {
      uploadImage(name, file, dataUrl);
    }
  };

  return (
    <div data-field-name={name} style={{ width: "100%" }}>
      {displayVariant === "file" && imageUrl ? (
        <>
          <DocumentFileButton label="Ver archivo" onClick={() => setIsViewerOpen(true)} />
          <DocumentImageViewer
            open={isViewerOpen}
            onClose={() => setIsViewerOpen(false)}
            imageUrl={imageUrl}
            alt={label}
            fileName={fileName ?? field.value instanceof File ? field.value.name : label}
            allowDelete={(displayVariant === "file" ? true : allowClear) && !readOnly}
            onDelete={() => {
              field.onChange(null);
            }}
          />
        </>
      ) : (
        <DocumentPicturePreview
          label={label}
          imageUrl={imageUrl}
          previewHeight={previewHeight}
          placeholderHeight={placeholderHeight}
          backgroundColor={backgroundColor}
          borderStyle={borderStyle}
          iconVariant={iconVariant}
          allowClear={allowClear && !readOnly}
          allowImageViewer={allowImageViewer}
          imageFileName={fileName ?? (field.value instanceof File ? field.value.name : label)}
          onUpload={handleUpload}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

export default FormDocumentPicturePreview;
